import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { tCell, Button, GenericTable, Input, Dropdown, Dialog, toastMessage } from "../utils";
import GenericDateRangeFilter from "../utils/GenericDateRangeFilter/GenericDateRangeFilter";
import { useOrgs, usePlant, useThirdPartyLogs, useUserDetail, useUserPermissions } from "../../services/hooks";
import { usePlantContext } from "../../context/plantContext";
import { findDeepValue } from "../../services/functions";
import { buttonClasses } from "../utils/theme";
import { formatDate as formatDateWithTimezone } from "../../services/functions";
import { Navigate } from "react-router-dom/dist";
import { useGetApiListDetailQuery } from "../../api/vmsSlice";
import dayjs from "dayjs";
import "./ThirdPartyLogs.css";

const defaultFilter = {
	pagesize: 10,
	page_no: 1,
};

const today = dayjs();
const startDate = today.subtract(1, "day");
const endDate = today;

function getApiValue(api = {}) {
	return `${api.domain || ""}${api.endPoint || ""}`;
}

const ThirdPartyLogs = () => {
	const location = useLocation();
	const { userDetail, isSuperAdmin } = useUserDetail();
	const { userPermissions, apiPermissions, isFetched } = useUserPermissions();
	const { plantId: contextPlantId, timezone } = usePlantContext();
	const formatDate = (date, format) => formatDateWithTimezone(date, format, timezone);
	const filterRef = useRef(defaultFilter);
	const { page_no, pagesize } = filterRef.current;
	const { logs, count, tableHeader, isLoading, getThirdPartyLogs } = useThirdPartyLogs();
	const initialCallDone = useRef(false);

	// --- Inline filter state (org, plant, date range) ---
	const [filter, setFilter] = useState({
		startDate,
		endDate,
		orgId: isSuperAdmin ? "" : userDetail?.orgId || "",
		plantId: !isSuperAdmin && contextPlantId ? contextPlantId : "",
	});

	const handleFilterChange = (name, value) => {
		// console.log(`Filter changed for ${name}:`, value);
		setFilter((o) => ({ ...o, [name]: value }));
	};

	const handleDateRangeChange = (range) => {
		if (!range) {
			setFilter((o) => ({ ...o, startDate: null, endDate: null }));
			return;
		}

		setFilter((o) => ({
			...o,
			startDate: range.startDate,
			endDate: range.endDate,
		}));
	};

	useEffect(() => {
		if (!isSuperAdmin && userDetail?.orgId) {
			setFilter((o) => ({ ...o, orgId: userDetail.orgId, plantId: contextPlantId || "" }));
		}
	}, [isSuperAdmin, userDetail, contextPlantId]);

	useEffect(() => {
		if (!location.state) return;
		const { orgId } = location.state;
		setFilter((o) => ({ ...o, orgId: orgId || o.orgId }));
	}, [location]);

	const { orgOptions } = useOrgs({}, { skip: !isSuperAdmin });
	const orgValue = orgOptions.find((_) => _.value == filter.orgId) || null;

	const { plantOptions, getPlantDetails, clearPlants } = usePlant();
	const plantValue = plantOptions.find((_) => _.value == filter.plantId) || null;

	const permittedApiIds = useMemo(() => {
		if (isSuperAdmin || !filter.plantId) return [];
		const prefix = `plant-${filter.plantId}-`;
		return [...new Set(Object.entries(apiPermissions || {})
			.filter(([key, value]) => key.startsWith(prefix) && value?.value)
			.map(([key]) => key.slice(prefix.length))
			.filter(Boolean))];
	}, [apiPermissions, filter.plantId, isSuperAdmin]);

	const apiListPayload = useMemo(() => {
		if (!filter.orgId) return {};
		const payload = { orgId: filter.orgId, limit: 1000, filters: {forLogs: true} };
		if (!isSuperAdmin) payload.filters = { ...payload.filters, _id: permittedApiIds };
		return payload;
	}, [filter.orgId, isSuperAdmin, permittedApiIds]);

	const shouldFetchApiList = !!filter.orgId && (isSuperAdmin || (!!filter.plantId && permittedApiIds.length));
	const { data: apiListData, isFetching: isApiListLoading } = useGetApiListDetailQuery(apiListPayload, { skip: !shouldFetchApiList });
	const apiList = apiListData?.data || [];
	const apiOptions = useMemo(() => {
		const seen = new Set();
		return apiList.reduce((options, api) => {
			const label = api.name || "";
			const value = getApiValue(api);
			const permissionKey = `plant-${filter.plantId}-${api._id}`;
			if (!label || !value || seen.has(label)) return options;
			if (!isSuperAdmin && !apiPermissions?.[permissionKey]?.value) return options;
			seen.add(label);
			options.push({ label, value, data: api });
			return options;
		}, []);
	}, [apiList, apiPermissions, filter.plantId, isSuperAdmin]);
	const apiValue = apiOptions.find((_) => _.value == filter.api_name) || null;

	useEffect(() => {
		if (filter.orgId) {
			getPlantDetails({ orgId: filter.orgId });
		} else {
			clearPlants();
		}
	}, [filter.orgId]);


	function handleSubmit() {
		if (isSuperAdmin && !filter.orgId) {
			toastMessage(false, "Please select an organization");
			return;
		}
		if (!filter.startDate || !filter.endDate) {
			toastMessage(false, "Please select a date range");
			return;
		}

		const filters = [];
		if (filter.plantId) {
			filters.push({ path: "plantId", value: filter.plantId });
		}
		if (filter.api_name) {
			filters.push({ path: "api_name", value: filter.api_name });
		}

		if(filter.vehicleNumber) {
			filters.push({ path: "vehicleNumber", value: filter.vehicleNumber });
		}


		filterRef.current = {
			...filterRef.current,
			startDate: filter.startDate?.$d == "Invalid Date" ? new Date().toISOString() : filter.startDate.toISOString(),
			endDate: filter.endDate?.$d == "Invalid Date" ? new Date().toISOString() : filter.endDate.toISOString(),
			orgId: filter.orgId,
			filters,
			page_no: 1,
		};

		getThirdPartyLogs(filterRef.current);
	}

	useEffect(() => {
		if (!filter.orgId || initialCallDone.current) return;
		initialCallDone.current = true;
		handleSubmit();
	}, [filter.orgId]);

	// --- Table rendering (uses tableHeader from listing API for columns) ---
	const [showJsonView, setShowJsonView] = useState(false);
	const jsonData = useRef({});

	let header = ["S.No", ...(tableHeader || []).map((_) => _.label)];

	let rows = logs?.map((data, i) => {
		let row = [tCell((page_no - 1) * pagesize + i + 1)];

		for (let item of tableHeader || []) {
			let value = findDeepValue(data, item.name.split(".")) ?? "";

			if (value && item.type === "object") {
				row.push(
					tCell(
						`${Object.keys(value).length} Fields`,
						"var(--clr-primary)",
						"pointer",
						() => {
							setShowJsonView(true);
							jsonData.current = value;
						},
						i
					)
				);
			} else {
				row.push(tCell(item.type === "date" ? formatDate(value, item.format) : (value !== "" ? String(value) : " - ")));
			}
		}

		return row;
	});

	const onPageChange = (page_no) => {
		filterRef.current = { ...filterRef.current, page_no: page_no + 1 };
		getThirdPartyLogs(filterRef.current);
	};
	const onPageSizeChange = (e) => {
		const { value } = e.target;
		filterRef.current = { ...filterRef.current, pagesize: value, page_no: 1 };
		getThirdPartyLogs(filterRef.current);
	};

	if (isFetched && !isSuperAdmin && !userPermissions?.viewApiLogs?.value) return <Navigate to="/not-authorized" replace />;

	return (
		<div className="contentpanel">
			<div className="cust-row flex-algn-cent">
				<div className="cust-col-5">
					<h1 className="title">API Logs</h1>
				</div>
			</div>
			<hr className="bgg" />

			<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
				{isSuperAdmin && (
					<Dropdown
						label="Organization"
						name="orgId"
						value={orgValue}
						options={orgOptions}
						onChange={(op) => {
							handleFilterChange("orgId", op?.value || "");
							handleFilterChange("plantId", "");
							handleFilterChange("api_name", "");
						}}
					/>
				)}
				{filter.orgId && (
					<Dropdown
						label="Plant"
						name="plantId"
						value={plantValue}
						options={plantOptions}
						onChange={(op) => {
							handleFilterChange("plantId", op?.value || "");
							handleFilterChange("api_name", "");
						}}
					/>
				)}

				<Dropdown
						label="Api Name"
						name="api_name"
						value={apiValue}
						options={apiOptions}
						loading={isApiListLoading}
						disabled={!filter.orgId || (!isSuperAdmin && !filter.plantId)}
						onChange={(op) => {
							handleFilterChange("api_name", op?.value || "");
						}}
					/>

				<Input
					label="Vehicle Number"
					name="vehicleNumber"
					value={filter.vehicleNumber}
					onChange={(e) => handleFilterChange("vehicleNumber", e.target.value)}
				/>
				<GenericDateRangeFilter
					initialStartDate={filter.startDate ? new Date(filter.startDate) : null}
					initialEndDate={filter.endDate ? new Date(filter.endDate) : null}
					onDateRangeSelect={handleDateRangeChange}
				/>

				<Button text="Submit" onClick={handleSubmit} style={{ ...buttonClasses.lynkitOrangeFill, width: "fit-content", height: "fit-content" }} disabled={isLoading} />
			</div>

			<div className="main_content">
				<GenericTable header={header} rows={rows} pageCount={count} pageNo={page_no} limit={pagesize} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} isLoading={isLoading} />
			</div>

			{showJsonView ? (
				<Dialog title="JSON Viewer" size="xl" open={showJsonView} handleClose={() => setShowJsonView(false)} noBtn>
					{parseJson(jsonData.current, true)}
				</Dialog>
			) : null}
		</div>
	);
};

export default ThirdPartyLogs;

function parseJson(obj, collapsible) {
	let ui = [];
	for (let item in obj) {
		ui.push(handleJsonItem(item, obj[item], collapsible));
	}
	return <div className="json">{ui}</div>;
}

function handleJsonItem(key, value, collapsible) {
	let type = Array.isArray(value) ? "array" : typeof value;

	if (typeof value === "object") {
		let html = [];
		for (let item in value) {
			html.push(handleJsonItem(item, value[item], collapsible));
		}
		return JsonItemCollapsible(key, type, type, collapsible, html);
	}
	return JsonItem(key, type == "string" ? `"${value}"` : value, type);
}

function JsonItem(key, value, type) {
	return (
		<div className="json__item" key={key + value + type}>
			<div className="json__key">{key}</div>
			<div className={`json__value json__value--${type}`}>{value}</div>
		</div>
	);
}

function JsonItemCollapsible(key, value, type, collapsible = false, children) {
	return (
		<label className="json__item json__item--collapsible">
			<input type="checkbox" className="json__toggle" defaultChecked={collapsible} />
			<div className="json__key">{key}</div>
			<div className={`json__value json__value--${type}`}>{value}</div>
			{children}
		</label>
	);
}
